/**
 * Chutt... - Sentiment Candlestick Chart (Style Meltwater)
 * Barres verticales vertes (haut) et rouges (bas) par jour, style candlestick financier.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
  Tooltip as RechartsTooltip, ReferenceLine,
} from 'recharts';
import { colors } from '../../styles/colors';

function SentimentCandlestickChart({ historyData, height = 240 }) {
  if (!historyData || historyData.length === 0) return null;

  // Transformer: negative_count en valeurs negatives pour le rendu candlestick
  const chartData = historyData.map(d => ({
    ...d,
    positive: d.positive_count || 0,
    negative: -(d.negative_count || 0),
    displayDate: new Date(d.date).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'short',
    }),
  }));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
        <defs>
          <linearGradient id="positiveBarGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.success} stopOpacity={0.9} />
            <stop offset="100%" stopColor={colors.success} stopOpacity={0.6} />
          </linearGradient>
          <linearGradient id="negativeBarGrad" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor={colors.danger} stopOpacity={0.9} />
            <stop offset="100%" stopColor={colors.danger} stopOpacity={0.6} />
          </linearGradient>
        </defs>

        <XAxis
          dataKey="displayDate"
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 10, fill: colors.gray400 }}
          interval="preserveStartEnd"
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fontSize: 11, fill: colors.gray400 }}
          tickFormatter={(val) => Math.abs(val)}
        />

        {/* Ligne zero pour separer positif/negatif */}
        <ReferenceLine y={0} stroke={colors.gray300} strokeWidth={1} />

        {/* Barres positives (vers le haut, vertes) */}
        <Bar
          dataKey="positive"
          fill="url(#positiveBarGrad)"
          radius={[3, 3, 0, 0]}
          maxBarSize={20}
          animationBegin={0}
          animationDuration={800}
          animationEasing="ease-out"
        />

        {/* Barres negatives (vers le bas, rouges) */}
        <Bar
          dataKey="negative"
          fill="url(#negativeBarGrad)"
          radius={[0, 0, 3, 3]}
          maxBarSize={20}
          animationBegin={100}
          animationDuration={800}
          animationEasing="ease-out"
        />

        <RechartsTooltip
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0]?.payload;
            return (
              <Box sx={{
                bgcolor: colors.charcoal, color: 'white',
                px: 1.5, py: 1, borderRadius: 1.5,
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
              }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)' }}>
                  {d?.displayDate}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  <Typography sx={{ color: colors.success, fontWeight: 600, fontSize: '0.8rem' }}>
                    +{d?.positive || 0}
                  </Typography>
                  <Typography sx={{ color: colors.danger, fontWeight: 600, fontSize: '0.8rem' }}>
                    -{Math.abs(d?.negative || 0)}
                  </Typography>
                </Box>
              </Box>
            );
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default SentimentCandlestickChart;
